// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuth,
  signInWithCustomToken,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { useWallet } from '@solana/wallet-adapter-react';
import app from '../pages/firebase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { publicKey, signMessage, disconnect, connected } = useWallet();
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [authInProgress, setAuthInProgress] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [authError, setAuthError] = useState('');
  // Indicates whether the authentication process has finished
  const [authFinished, setAuthFinished] = useState(false);

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setFirebaseUser(user);
        setAuthSuccess(true);
        console.log('Firebase user:', user);
        // If the user exists, mark authentication as finished
        setAuthFinished(true);
      } else {
        setFirebaseUser(null);
        // If the user is not authenticated and the wallet is connected, proceed with authentication
        if (connected && publicKey && signMessage && typeof signMessage === 'function') {
          authenticateWithFirebase();
        } else {
          // If the wallet is not connected or in a connected state, mark authentication as finished
          setAuthFinished(true);
        }
      }
    });
    // Consider adding 'connected' to the dependency array to re-run when the wallet connection changes
  }, [publicKey, connected, signMessage]);

  const authenticateWithFirebase = async () => {
    console.log('Starting authenticateWithFirebase');
    const auth = getAuth(app);
    // If already signed in, do nothing
    if (auth.currentUser) {
      console.log('Already signed in to Firebase. Skipping authentication.');
      setAuthFinished(true);
      setFirebaseUser(auth.currentUser);
      return;
    }
    // If the wallet does not support signMessage, skip authentication
    if (!signMessage || typeof signMessage !== 'function') {
      console.warn("Wallet doesn't support signMessage or is disconnected. Skipping authentication.");
      setAuthFinished(true);
      return;
    }

    // Capture signMessage locally
    const localSignMessage = signMessage;

    setAuthInProgress(true);
    setAuthSuccess(false);
    setAuthError('');
    setAuthFinished(false);

    try {
      const pubKeyStr = publicKey.toString();
      console.log('publicKey:', pubKeyStr);

      // Step 1: Get nonce from /api/getNonce
      const nonceResponse = await fetch('/api/getNonce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicKey: pubKeyStr }),
      });
      const nonceData = await nonceResponse.json();
      const { nonce } = nonceData;
      console.log('Retrieved nonce:', nonce);
      if (!nonce) throw new Error('Failed to obtain nonce');

      // Check wallet connection again before signing
      if (!connected) {
        console.warn('Wallet got disconnected before signing. Aborting authentication.');
        setAuthFinished(true);
        return;
      }

      // Step 2: Sign the nonce
      const message = new TextEncoder().encode(nonce);
      console.log('Message to be signed:', message);
      const signatureUint8 = await localSignMessage(message);
      console.log('Signature result (Uint8Array):', signatureUint8);
      const signatureHex = Array.from(signatureUint8)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      console.log('Signature result (Hex):', signatureHex);

      // Step 3: Verify the signature with /api/verifySignature
      const verifyResponse = await fetch('/api/verifySignature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          publicKey: pubKeyStr,
          nonce,
          signature: signatureHex,
        }),
      });
      const verifyData = await verifyResponse.json();
      console.log('Signature verification result:', verifyData);
      const { firebaseToken } = verifyData;
      if (!firebaseToken) throw new Error('Signature verification failed');

      // Step 4: Sign in with Firebase custom token
      await signInWithCustomToken(auth, firebaseToken);
      console.log('Firebase user after signInWithCustomToken:', auth.currentUser);
      setFirebaseUser(auth.currentUser);
      setAuthSuccess(true);
    } catch (error) {
      console.error('Firebase authentication error:', error);
      setAuthError(error.message || 'Firebase authentication failed');
      disconnect(); // Disconnect wallet on authentication failure
    } finally {
      setAuthInProgress(false);
      setAuthFinished(true);
    }
  };

  // Sign out: disconnect wallet, sign out of Firebase, and reset state
  const handleSignOut = async () => {
    try {
      // Disconnect wallet
      disconnect();
      // Sign out of Firebase
      const auth = getAuth(app);
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign-out error:', error);
    } finally {
      // Reset state
      setFirebaseUser(null);
      setAuthSuccess(false);
      setAuthError('');
      setAuthInProgress(false);
      setAuthFinished(true);
    }
  };

  const value = {
    publicKey,
    firebaseUser,
    authInProgress,
    authSuccess,
    authError,
    authFinished,
    authenticateWithFirebase,
    handleSignOut, // Added sign-out function to the context
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
